import React, { memo, useState, useMemo, useCallback } from 'react';
import {
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	TableSortLabel,
	Paper,
	Typography,
	Box,
} from '@mui/material';
import { useIntl } from 'react-intl';
import { formatIntervalToReadable } from 'src/utils/breakReason';
import BreakReasonOnlyTimer from './break-reason-only-timer';

// Colunas com suporte a sorting
const SORTABLE_COLUMNS = {
	operator: (row) => row.user?.name?.toLowerCase() || '',
	userTeams: (row) => row.userTeams?.toLowerCase() || '',
	reason: (row) => row.reasonbreak?.name?.toLowerCase() || '',
	start: (row) => row.startdateOriginal ? new Date(row.startdateOriginal).getTime() : 0,
};

// Estilos inline para performance (evita recriação de objetos)
const styles = {
	container: {},
	headerCell: {
		fontWeight: 600,
		backgroundColor: '#f5f5f5',
		position: 'sticky',
		top: 0,
		zIndex: 1,
	},
	sortLabel: {
		'&.MuiTableSortLabel-root': {
			color: 'inherit',
		},
	},
	row: {
		'&:nth-of-type(odd)': {
			backgroundColor: '#fafafa',
		},
		'&:hover': {
			backgroundColor: '#f0f0f0',
		},
	},
	emptyMessage: {
		textAlign: 'center',
		padding: 16,
		color: '#666',
	},
};

/**
 * Linha individual da tabela - memoizada para evitar re-renders desnecessários
 */
const BreakTableRow = memo(function BreakTableRow({ row }) {
	return (
		<TableRow sx={styles.row}>
			<TableCell>{row.user?.name || '-'}</TableCell>
			<TableCell>{row.userTeams || 'Sem time'}</TableCell>
			<TableCell>{row.reasonbreak?.name || '-'}</TableCell>
			<TableCell>{row.startdate || '-'}</TableCell>
			<TableCell>
				<BreakReasonOnlyTimer breakWorkData={row} />
			</TableCell>
			<TableCell>{formatIntervalToReadable(row.breaklimit)}</TableCell>
			<TableCell>
				<BreakReasonOnlyTimer breakWorkData={row} isOverflowTime />
			</TableCell>
		</TableRow>
	);
}, (prevProps, nextProps) => {
	// Só re-renderiza se o ID ou dados relevantes mudarem
	return (
		prevProps.row?.id === nextProps.row?.id &&
		prevProps.row?.startdateOriginal === nextProps.row?.startdateOriginal &&
		prevProps.row?.breaklimitOriginal === nextProps.row?.breaklimitOriginal
	);
});

/**
 * Tabela básica otimizada para listagem de pausas ativas
 * 
 * Diferente do MyMaterialTable (material-table), este componente:
 * - Usa componentes MUI nativos (mais leves)
 * - Memoiza linhas individualmente
 * - Sorting leve em colunas selecionadas
 * - Otimizado para atualizações frequentes via SignalR
 */
function BreakWorkBasicTable({ data = [] }) {
	const intl = useIntl();
	const [orderBy, setOrderBy] = useState(null);
	const [order, setOrder] = useState('asc');

	// Handler de sorting memoizado
	const handleSort = useCallback((column) => {
		setOrderBy((prevOrderBy) => {
			if (prevOrderBy === column) {
				// Mesma coluna: alterna direção ou remove sorting
				setOrder((prevOrder) => {
					if (prevOrder === 'asc') return 'desc';
					// Se já está desc, remove o sorting
					setOrderBy(null);
					return 'asc';
				});
				return column;
			}
			// Nova coluna: começa ascendente
			setOrder('asc');
			return column;
		});
	}, []);

	// Dados ordenados (memoizado para evitar sorting desnecessário)
	const sortedData = useMemo(() => {
		if (!orderBy || !SORTABLE_COLUMNS[orderBy]) return data;

		const getValue = SORTABLE_COLUMNS[orderBy];
		return [...data].sort((a, b) => {
			const aVal = getValue(a);
			const bVal = getValue(b);
			
			if (aVal < bVal) return order === 'asc' ? -1 : 1;
			if (aVal > bVal) return order === 'asc' ? 1 : -1;
			return 0;
		});
	}, [data, orderBy, order]);

	// Helper para renderizar header com sorting
	const SortableHeader = ({ column, children }) => (
		<TableCell sx={styles.headerCell}>
			<TableSortLabel
				active={orderBy === column}
				direction={orderBy === column ? order : 'asc'}
				onClick={() => handleSort(column)}
				sx={styles.sortLabel}
			>
				{children}
			</TableSortLabel>
		</TableCell>
	);

	if (!data || data.length === 0) {
		return (
			<Box sx={{ marginTop: 2 }}>
				<Typography sx={styles.emptyMessage} variant="h6">
					{intl.formatMessage({ id: "rows.show" })}
				</Typography>
			</Box>
		);
	}

	return (
		<TableContainer sx={styles.container}>
			<Table stickyHeader size="small" aria-label="tabela de pausas ativas">
				<TableHead>
					<TableRow>
						<SortableHeader column="operator">
							{intl.formatMessage({ id: "menu.breakwork.collaborators.active.columns.operator" })}
						</SortableHeader>
						<SortableHeader column="userTeams">
							{intl.formatMessage({ id: "menu.techinicalteams" })}
						</SortableHeader>
						<SortableHeader column="reason">
							{intl.formatMessage({ id: "menu.breakwork.collaborators.active.columns.reason" })}
						</SortableHeader>
						<SortableHeader column="start">
							{intl.formatMessage({ id: "menu.breakwork.collaborators.active.columns.start" })}
						</SortableHeader>
						<TableCell sx={styles.headerCell}>
							{intl.formatMessage({ id: "menu.breakwork.collaborators.active.columns.duration" })}
						</TableCell>
						<TableCell sx={styles.headerCell}>
							{intl.formatMessage({ id: "menu.breakwork.collaborators.active.columns.limit" })}
						</TableCell>
						<TableCell sx={styles.headerCell}>
							{intl.formatMessage({ id: "menu.breakwork.collaborators.active.columns.exceeded" })}
						</TableCell>
					</TableRow>
				</TableHead>
				<TableBody>
					{sortedData.map((row) => (
						<BreakTableRow key={row.id} row={row} />
					))}
				</TableBody>
			</Table>
		</TableContainer>
	);
}

// Memoiza o componente principal
export default memo(BreakWorkBasicTable, (prevProps, nextProps) => {
	// Compara arrays por referência primeiro (mais rápido)
	if (prevProps.data === nextProps.data) return true;

	// Se tamanhos diferentes, precisa re-render
	if (prevProps.data?.length !== nextProps.data?.length) return false;

	// Compara IDs para detectar mudanças na lista
	const prevIds = prevProps.data?.map(b => b.id).join(',') || '';
	const nextIds = nextProps.data?.map(b => b.id).join(',') || '';

	return prevIds === nextIds;
});
